import { EAttributeType, EAttributeUnit } from "../@types/AttributeTypes";
import { IBuildingProviderContext } from "../@types/ContextProviderRelated";
import AttributeModel from "../models/standard/AttributeModel";
import { convertAttributeValue, getCorrespondingUnit, IMPERIAL_TO_METRIC, METRIC_SCALE_FACTORS } from "./attributes/attributeUtils";

/**
 * Enum for the Supported Regions in the application.
 * These are the high-level buckets that attributes/settings fall into.
 */
 export enum ERegionCode {
    DE = 'DE',
    CH = 'CH',
    GB = 'GB',
    US = 'US',
    GLOBAL = 'GLOBAL',
  }
  
  /**
   * Enum for Country Codes potentially returned by the User API.
   */
  export enum ECountryCode {
    DE = 'DE',
    AT = 'AT', // Austria
    CH = 'CH',
    DK = 'DK', // Denmark
    GB = 'GB',
    IT = 'IT', // Italy
    LI = 'LI', // Liechtenstein
    LU = 'LU', // Luxembourg
    US = 'US',
    GLOBAL = 'GLOBAL', 
  }
  
  /**
   * Mapping table to convert specific Country Codes to their associated Region.
   * Uses ERegionCode as values to ensure type safety.
   */
  export const COUNTRY_TO_REGION: Record<string, ERegionCode> = {
    // --- Direct Mappings ---
    [ECountryCode.CH]: ERegionCode.CH,
    [ECountryCode.GB]: ERegionCode.GB,
    'UK': ERegionCode.GB, // Legacy handling
    [ECountryCode.US]: ERegionCode.US,
    [ECountryCode.GLOBAL]: ERegionCode.GLOBAL,
  
    // --- Fallbacks to DE /---
    [ECountryCode.DE]: ERegionCode.DE,
    [ECountryCode.DK]: ERegionCode.DE, // Denmark -> DE
    [ECountryCode.AT]: ERegionCode.DE, // Austria -> DE
    [ECountryCode.IT]: ERegionCode.DE, // Italy -> DE
    [ECountryCode.LI]: ERegionCode.CH, // Liechtenstein -> CH
    [ECountryCode.LU]: ERegionCode.DE, // Luxembourg -> DE
  };

export const isRegionImperial = (region: string): boolean => {
  return region === ERegionCode.US;
};

/**
 * Recursively updates attributes in the building to match the target region's unit system.
 * Preserves Class Prototypes (Methods) for all models.
 */
export const convertBuildingAttributesToRegion = (
  building: IBuildingProviderContext,
  targetRegion: string
): IBuildingProviderContext => {
  const toImperial = isRegionImperial(targetRegion);

  // --- 1. Helper: Convert a list of AttributeModels ---
  const convertAttributes = (attrs: AttributeModel[] = []): AttributeModel[] => {
    return attrs.map((attr) => {
      // Only convert NUMBER types that have a unit
      if (attr.type !== EAttributeType.NUMBER || !attr.unit) {
        return attr;
      }

      const currentUnit = attr.unit as EAttributeUnit;
      const targetUnit = getCorrespondingUnit(currentUnit, toImperial);

      // If unit is already correct for the region, skip
      if (currentUnit === targetUnit) {
        return attr;
      }

      const rawVal = parseFloat(String(attr.value));
      if (isNaN(rawVal)) return attr;

      // A. Basic Conversion
      // If toImperial=true: Metric -> Imperial Unit (Correct)
      // If toImperial=false: Imperial -> Base Metric (Meters) (Needs scaling!)
      let convertedVal = convertAttributeValue(rawVal, currentUnit, toImperial);

      // B. Magnitude Fix (Imperial -> Metric)
      // convertAttributeValue returns Meters. We must scale it to the target (e.g. CM).
      if (!toImperial && IMPERIAL_TO_METRIC[currentUnit]) {
          const scale = METRIC_SCALE_FACTORS[targetUnit] || 1;
          convertedVal = convertedVal / scale;
      }

      const roundedVal = Math.round(convertedVal * 10000) / 10000;

      // Return new Attribute instance
      return new AttributeModel(
        attr.id,
        attr.key,
        roundedVal, 
        attr.predecessorId,
        attr.type,
        targetUnit, // New Unit
        attr.hidden,
        attr.forced,
        attr.ownerId,
        attr.userId,
        attr.publicFlag,
        attr.localization,
        attr.defaults,
        attr.validation,
        attr.unitFamily
      );
    });
  };

  // --- 2. Helper: Clone Entity & Preserve Prototype ---
  // This ensures methods like 'setOuterPoints' are kept on the new object.
  const cloneEntity = (original: any, overrides: Partial<any>): any => {
    if (!original) return original;
    // Create new object with same prototype
    const clone = Object.create(Object.getPrototypeOf(original));
    // Copy properties
    Object.assign(clone, original);
    // Apply overrides
    Object.assign(clone, overrides);
    return clone;
  };

  // --- 3. Model Converters ---

  // RadiatorModel (Nested in Wall Sides)
  const convertRadiator = (rad: any) => {
    return cloneEntity(rad, { 
        attributes: convertAttributes(rad.attributes) 
    });
  };

  // WindowModel
  const convertWindow = (win: any) => {
    return cloneEntity(win, { 
        attributes: convertAttributes(win.attributes) 
    });
  };

  // DoorModel
  const convertDoor = (door: any) => {
    return cloneEntity(door, { 
        attributes: convertAttributes(door.attributes) 
    });
  };

  const convertPassage = (passage: any) => {
    return cloneEntity(passage, { 
        attributes: convertAttributes(passage.attributes) 
    });
  };

  // WallModel (Standard & Partition)
  const convertWall = (wall: any) => {
    const newAttrs = convertAttributes(wall.attributes);
    const overrides: any = { attributes: newAttrs };

    // Handle StandardWall Children
    if (Array.isArray(wall.windows)) {
       overrides.windows = wall.windows.map(convertWindow);
    }
    if (Array.isArray(wall.doors)) {
       overrides.doors = wall.doors.map(convertDoor);
    }
    if (Array.isArray(wall.passages)) {
       overrides.passages = wall.passages.map(convertPassage);
    }
    
    // Handle Radiators (inside 'sides' object A/B of StandardWall)
    if (wall.sides) {
        // Updated to handle object structure (A/B), not array
        overrides.sides = {
          A: { 
            ...wall.sides.A, 
            radiators: Array.isArray(wall.sides.A?.radiators) ? wall.sides.A.radiators.map(convertRadiator) : [] 
          },
          B: { 
            ...wall.sides.B, 
            radiators: Array.isArray(wall.sides.B?.radiators) ? wall.sides.B.radiators.map(convertRadiator) : [] 
          }
        };
    }

    return cloneEntity(wall, overrides);
  };

  // ElementModel
  const convertElement = (el: any) => {
      // Element attributes are usually inside the 'object' property (IObject)
      let newObject = el.object;
      if (el.object && el.object.attributes) {
          const newObjAttrs = convertAttributes(el.object.attributes);
          // Clone the ObjectModel inside the element
          newObject = cloneEntity(el.object, { attributes: newObjAttrs });
      }
      return cloneEntity(el, { object: newObject });
  };

  // FloorModel
  const convertFloor = (floor: any) => {
      return cloneEntity(floor, { 
          attributes: convertAttributes(floor.attributes) 
      });
  };

  // SpaceModel
  const convertSpace = (space: any) => {
      // Handle single floor 1:1 (fallback to legacy array[0] if needed)
      const rawFloor = space.floor;
      const newFloor = rawFloor ? convertFloor(rawFloor) : undefined;
      
      return cloneEntity(space, { floor: newFloor });
  };

  // RoomModel
  const convertRoom = (room: any) => {
      const newAttrs = convertAttributes(room.attributes);
      const newSpaces = (room.spaces || []).map(convertSpace);
      return cloneEntity(room, { 
          attributes: newAttrs, 
          spaces: newSpaces 
      });
  };


  // --- 4. Execute Conversion on Building Context ---
  
  return {
      ...building,
      // Map Walls
      walls: building.walls.map(convertWall),
      // Map Elements
      elements: building.elements.map(convertElement),
      // Map Rooms -> Spaces -> Floor
      rooms: building.rooms.map(convertRoom),
      models2D: building.models2D,
      products: building.products 
  };
};
